import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './SalesReport.module.css';
import { API_BASE_URL } from "../Config.js";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Download,
  GripVertical,
  IndianRupee,
  Layers,
  Package,
  PackageSearch,
  Plus,
  Receipt,
  X,
} from 'lucide-react';

const FIELD_OPTIONS = [
  { value: 'itemType', label: 'Item Type' },
  { value: 'description', label: 'Item Category' },
  { value: 'itemSize', label: 'Size' },
  { value: 'itemColor', label: 'Item Color' },
];

const ORDER_OPTIONS = [
  { value: 'asc', label: 'Ascending', hint: 'A → Z · 0 → 9', icon: ArrowUp },
  { value: 'desc', label: 'Descending', hint: 'Z → A · 9 → 0', icon: ArrowDown },
];

// Display-only formatting with Indian digit grouping (the export sends raw data)
const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatCount = (value) => Number(value || 0).toLocaleString('en-IN');

const SalesReport = ({ data, loading = false }) => {
  const [billTypeFilter, setBillTypeFilter] = useState('RETAIL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedSourceField, setDraggedSourceField] = useState(null);

  // Stack: priority (first = highest)
  const [stack, setStack] = useState([]);

  // Global Asc / Desc
  const [globalOrder, setGlobalOrder] = useState("asc");

  // unique ID generator
  const nextId = (() => {
    let n = 1;
    return () => `lvl_${n++}`;
  })();

  // Add a new level to stack from available fields
  const addLevelFromField = (field) => {
    if (!field) return;
    if (stack.some(s => s.field === field)) return;

    const id = nextId();
    setStack(prev => [...prev, { id, field }]);
  };

  // Remove a level
  const removeLevel = (id) => {
    setStack(prev => prev.filter(s => s.id !== id));
  };

  // Drag handlers for stack reordering
  const handleStackDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStackDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    e.dataTransfer.dropEffect = 'move';

    const newStack = [...stack];
    const draggedItem = newStack[draggedIndex];
    newStack.splice(draggedIndex, 1);
    newStack.splice(index, 0, draggedItem);

    setStack(newStack);
    setDraggedIndex(index);
  };

  const handleStackDrop = (e, dropIndex) => {
    e.preventDefault();
    setDraggedIndex(null);
  };

  const handleStackDragEnd = () => {
    setDraggedIndex(null);
  };

  // Drag handlers for available fields
  const handleFieldDragStart = (e, field) => {
    setDraggedSourceField(field);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleFieldDragEnd = () => {
    setDraggedSourceField(null);
  };

  // Drop zone for stack area
  const handleStackAreaDragOver = (e) => {
    e.preventDefault();
    if (draggedSourceField) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleStackAreaDrop = (e) => {
    e.preventDefault();
    if (draggedSourceField) {
      addLevelFromField(draggedSourceField);
      setDraggedSourceField(null);
    }
  };

  // Close the sort modal on Escape
  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsModalOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  // Filter existing logic
  const filteredData = Array.isArray(data)
    ? data.filter(item => item.totalQuantity > 0 && (billTypeFilter === 'Both' || item.billType === billTypeFilter))
    : [];

  // Sorting logic using stack priority + global asc/desc
  const sortedData = useMemo(() => {
    if (stack.length === 0) return [...filteredData];

    return [...filteredData].sort((a, b) => {
      for (const level of stack) {
        const field = level.field;

        const va = a[field] ?? "";
        const vb = b[field] ?? "";

        const sa = typeof va === "string" ? va.toLowerCase() : va;
        const sb = typeof vb === "string" ? vb.toLowerCase() : vb;

        let cmp = 0;
        if (sa < sb) cmp = -1;
        else if (sa > sb) cmp = 1;

        if (cmp !== 0) {
          return globalOrder === "asc" ? cmp : -cmp;
        }
      }
      return 0;
    });
  }, [filteredData, stack, globalOrder]);

  const totalAmountSum = sortedData.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);

  // Export
  const handleExport = async () => {
    if (sortedData.length === 0) {
      alert('No data to export.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/sales/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sortedData),
      });

      if (!response.ok) throw new Error('Failed to export sales report.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sales_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const availableOptions = FIELD_OPTIONS.filter(opt => !stack.some(s => s.field === opt.value));

  const fieldLabel = (field) => FIELD_OPTIONS.find(f => f.value === field)?.label || field;
  const totalQuantitySum = sortedData.reduce((sum, item) => sum + (Number(item.totalQuantity) || 0), 0);
  const itemTypeCount = new Set(sortedData.map(item => item.itemType).filter(Boolean)).size;
  const billLabel = billTypeFilter === 'Both'
    ? 'All bills'
    : `${billTypeFilter.charAt(0)}${billTypeFilter.slice(1).toLowerCase()} bills`;

  const summaryCards = [
    { key: 'amount', label: 'Total amount', value: `₹${formatAmount(totalAmountSum)}`, icon: IndianRupee, primary: true },
    { key: 'qty', label: 'Quantity sold', value: formatCount(totalQuantitySum), icon: Package },
    { key: 'rows', label: 'Line items', value: formatCount(sortedData.length), icon: Receipt },
    { key: 'types', label: 'Item types', value: formatCount(itemTypeCount), icon: Layers },
  ];

  // Rendered into document.body: the page uses a CSS container, which would
  // otherwise trap this fixed-position overlay inside the page area.
  const sortModal = isModalOpen && createPortal(
    <div className={styles.modalOverlay} onClick={() => setIsModalOpen(false)}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sales-sort-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div>
            <h3 id="sales-sort-title" className={styles.modalTitle}>Sort rows</h3>
            <p className={styles.modalSubtitle}>
              Add fields in priority order — rows are sorted by the first field, then the next.
            </p>
          </div>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => setIsModalOpen(false)}
            aria-label="Close"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Available Fields Section */}
          <div>
            <p className={styles.sectionLabel}>
              Available fields
              <span className={styles.sectionHint}>click or drag to add</span>
            </p>
            <div className={styles.fieldPills}>
              {availableOptions.map(opt => (
                <div
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  className={`${styles.fieldPill} ${draggedSourceField === opt.value ? styles.fieldPillDragging : ''}`}
                  draggable
                  onDragStart={(e) => handleFieldDragStart(e, opt.value)}
                  onDragEnd={handleFieldDragEnd}
                  onClick={() => addLevelFromField(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      addLevelFromField(opt.value);
                    }
                  }}
                >
                  <Plus size={14} aria-hidden="true" />
                  {opt.label}
                </div>
              ))}
              {availableOptions.length === 0 && (
                <span className={styles.emptyHint}>All fields added to sorting</span>
              )}
            </div>
          </div>

          {/* Stack Section */}
          <div>
            <p className={styles.sectionLabel}>
              Sorting priority
              <span className={styles.sectionHint}>drag to reorder</span>
            </p>
            <div
              className={`${styles.dropZone} ${draggedSourceField ? styles.dropZoneActive : ''}`}
              onDragOver={handleStackAreaDragOver}
              onDrop={handleStackAreaDrop}
            >
              {stack.length === 0 ? (
                <div className={styles.dropZoneEmpty}>
                  Click or drag fields here to set sorting priority
                </div>
              ) : (
                <div className={styles.stackList}>
                  {stack.map((s, i) => {
                    const label = fieldLabel(s.field);
                    return (
                      <div
                        key={s.id}
                        className={`${styles.stackItem} ${draggedIndex === i ? styles.stackItemDragging : ''}`}
                        draggable
                        onDragStart={(e) => handleStackDragStart(e, i)}
                        onDragOver={(e) => handleStackDragOver(e, i)}
                        onDrop={(e) => handleStackDrop(e, i)}
                        onDragEnd={handleStackDragEnd}
                      >
                        <GripVertical size={16} className={styles.stackGrip} aria-hidden="true" />
                        <span className={styles.stackBadge}>{i + 1}</span>
                        <span className={styles.stackLabel}>{label}</span>
                        <button
                          type="button"
                          className={styles.stackRemove}
                          onClick={() => removeLevel(s.id)}
                          aria-label={`Remove ${label}`}
                        >
                          <X size={15} aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Global Sorting Order */}
          <div role="radiogroup" aria-labelledby="sales-sort-order">
            <p id="sales-sort-order" className={styles.sectionLabel}>Sorting order</p>
            <div className={styles.orderGrid}>
              {ORDER_OPTIONS.map(({ value, label, hint, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={globalOrder === value}
                  className={`${styles.orderCard} ${globalOrder === value ? styles.orderCardOn : ''}`}
                  onClick={() => setGlobalOrder(value)}
                >
                  <Icon size={18} aria-hidden="true" />
                  <span className={styles.orderText}>
                    <span className={styles.orderTitle}>{label}</span>
                    <span className={styles.orderHint}>{hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => setIsModalOpen(false)}
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {sortModal}

      <div className={styles.root}>
        {/* Summary cards */}
        <div className={styles.summaryGrid}>
          {summaryCards.map(({ key, label, value, icon: Icon, primary }) => (
            <div key={key} className={`${styles.summaryCard} ${primary ? styles.summaryCardPrimary : ''}`}>
              <span className={styles.summaryIcon} aria-hidden="true">
                <Icon size={18} />
              </span>
              <div className={styles.summaryText}>
                <span className={styles.summaryLabel}>{label}</span>
                <span className={styles.summaryValue}>{value}</span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarInfo}>
              <span className={`${styles.chip} ${styles.chipNeutral}`}>{billLabel}</span>
              {stack.length > 0 ? (
                <span className={styles.chip}>
                  <ArrowUpDown size={13} aria-hidden="true" />
                  {stack.map(s => fieldLabel(s.field)).join(' → ')} · {globalOrder === 'asc' ? 'Ascending' : 'Descending'}
                </span>
              ) : (
                <span className={styles.chipMuted}>Original order</span>
              )}
            </div>

            <div className={styles.toolbarActions}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnGhost} ${stack.length > 0 ? styles.btnGhostOn : ''}`}
                onClick={() => setIsModalOpen(true)}
                disabled={sortedData.length === 0}
              >
                <ArrowUpDown size={15} aria-hidden="true" />
                Filter &amp; Sort
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleExport}
                disabled={sortedData.length === 0}
              >
                <Download size={15} aria-hidden="true" />
                Export
              </button>
            </div>
          </div>

          {/* TABLE */}
          <div className={styles.tableWrap}>
            {loading && (
              <div className={styles.loadingOverlay}>
                <span className={styles.spinner} aria-hidden="true" />
              </div>
            )}

            {sortedData.length > 0 ? (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Bill Type</th>
                    <th>Description</th>
                    <th>Item Type</th>
                    <th>Item Color</th>
                    <th>Size</th>
                    <th className={styles.num}>Price</th>
                    <th className={styles.num}>Avg Sell Price</th>
                    <th className={styles.num}>Quantity</th>
                    <th className={styles.num}>Amount</th>
                    <th>Item Desc</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item, index) => (
                    <tr key={index}>
                      <td><span className={styles.billTag}>{item.billType}</span></td>
                      <td className={styles.strong}>
                        <span className={styles.truncate} title={item.description}>{item.description}</span>
                      </td>
                      <td>{item.itemType}</td>
                      <td>{item.itemColor}</td>
                      <td>{item.itemSize}</td>
                      <td className={styles.num}>{formatAmount(item.price)}</td>
                      <td className={styles.num}>{formatAmount(item.sellPrice)}</td>
                      <td className={styles.num}>{item.totalQuantity}</td>
                      <td className={`${styles.num} ${styles.strong}`}>{formatAmount(item.totalAmount)}</td>
                      <td>
                        <span className={styles.truncate} title={item.desc}>{item.desc}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="7">Total</td>
                    <td className={styles.num}>{formatCount(totalQuantitySum)}</td>
                    <td className={styles.num}>{formatAmount(totalAmountSum)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            ) : (
              <div className={styles.noResults}>
                <span className={styles.noResultsIcon} aria-hidden="true">
                  <PackageSearch size={28} />
                </span>
                <p className={styles.noResultsTitle}>No sales to show</p>
                <p className={styles.noResultsText}>
                  Only {billLabel.toLowerCase()} with a quantity above zero are listed here.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesReport;
